import React, { useState } from 'react';
import { ThemeProvider } from './context/ThemeContext';
import { AuthProvider, useAuth } from './context/AuthContext';
import { Shell } from './components/layout/Shell';
import { NavViewId } from './components/layout/Sidebar';

import { LoginView } from './views/LoginView';
import { DashboardView } from './views/DashboardView';
import { UsersView } from './views/UsersView';
import { DeletedAccountsView } from './views/DeletedAccountsView';
import { EscrowLedgerView } from './views/EscrowLedgerView';
import { DisputesView } from './views/DisputesView';
import { ListingsView } from './views/ListingsView';
import { CMSView } from './views/CMSView';
import { BroadcastView } from './views/BroadcastView';
import { SupportTicketsView } from './views/SupportTicketsView';
import { AuditLogsView } from './views/AuditLogsView';
import { AdminManagementView } from './views/AdminManagementView';
import { EmailTemplatesView } from './views/EmailTemplatesView';
import { ProfileSettingsView, ProfileTab } from './views/ProfileSettingsView';
import { VouchersView } from './views/VouchersView';
import { AdminChatView } from './views/AdminChatView';

import { ShieldAlert, ArrowLeft } from 'lucide-react';
import { ROLE_PERMITTED_VIEWS, ROLE_DISPLAY_NAMES, AdminRole } from './types/admin';

const AccessRestrictedView: React.FC<{ 
  userRole: AdminRole; 
  onReturnDashboard: () => void;
}> = ({ userRole, onReturnDashboard }) => {
  return (
    <div className="min-h-[60vh] flex flex-col items-center justify-center text-center p-6 animate-fadeIn">
      <div className="w-16 h-16 rounded-3xl bg-rose-500/15 border border-rose-500/30 flex items-center justify-center text-rose-400 mb-5 shadow-glow-sm">
        <ShieldAlert className="w-8 h-8" />
      </div>
      <span className="text-[11px] font-extrabold uppercase tracking-wider text-rose-400 px-3 py-1 rounded-full bg-rose-500/10 border border-rose-500/20 mb-3">
        Access Restricted • RBAC Tier Enforcement
      </span>
      <h2 className="text-2xl sm:text-3xl font-extrabold text-white dark:text-white light:text-[#0D1C17] tracking-tight">
        Insufficient Administrative Privileges
      </h2>
      <p className="mt-2 text-sm text-[#90cbb7] dark:text-[#90cbb7] light:text-[#527063] max-w-md leading-relaxed">
        Your active session is provisioned with <strong className="text-white dark:text-white light:text-[#0D1C17] font-semibold">{ROLE_DISPLAY_NAMES[userRole] || userRole}</strong> clearance. This module is restricted to authorized operational staff or the Super Admin.
      </p>
      <div className="mt-6 flex items-center gap-3">
        <button
          type="button"
          onClick={onReturnDashboard}
          className="px-5 py-2.5 rounded-2xl bg-brand-500 hover:bg-brand-400 text-forest-void font-bold text-xs shadow-glow-sm transition-all flex items-center gap-2"
        >
          <ArrowLeft className="w-4 h-4" />
          <span>Return to Your Authorized Dashboard</span>
        </button>
      </div>
    </div>
  );
};

const AdminApp: React.FC = () => {
  const { currentUser, loading } = useAuth();
  const [activeView, setActiveView] = useState<NavViewId>('dashboard');
  const [profileTab, setProfileTab] = useState<ProfileTab>('profile');

  // Loading screen
  if (loading) {
    return (
      <div className="min-h-screen w-full flex flex-col items-center justify-center bg-[#08130f] text-white space-y-4">
        <div className="w-12 h-12 rounded-2xl bg-brand-500 flex items-center justify-center text-forest-void shadow-glow-md animate-pulse">
          <span className="font-extrabold text-xl">R</span>
        </div>
        <p className="text-xs font-semibold text-[#90cbb7]">Authenticating ReUseIt Staff Session...</p>
      </div>
    );
  }

  // If not logged in, render the login & password reset portal
  if (!currentUser) {
    return <LoginView />;
  }

  const handleNavigateToProfile = (tab: ProfileTab) => {
    setProfileTab(tab);
    setActiveView('profile_settings');
  };

  const permittedViews = ROLE_PERMITTED_VIEWS[currentUser.role] || ROLE_PERMITTED_VIEWS.super_admin;

  const renderCurrentView = (view: NavViewId) => {
    // RBAC Security Guard: Block unauthorized direct navigation
    if (!permittedViews.includes(view)) {
      return (
        <AccessRestrictedView 
          userRole={currentUser.role} 
          onReturnDashboard={() => setActiveView('dashboard')} 
        />
      );
    }

    switch (view) {
      case 'dashboard':
        return <DashboardView onNavigate={(v) => setActiveView(v)} />;
      case 'users':
        return <UsersView />;
      case 'deleted_accounts':
        return <DeletedAccountsView />;
      case 'escrow':
        return <EscrowLedgerView />;
      case 'vouchers':
        return <VouchersView />;
      case 'disputes':
        return <DisputesView />;
      case 'listings':
        return <ListingsView />;
      case 'cms':
        return <CMSView />;
      case 'broadcast':
        return <BroadcastView />;
      case 'support':
        return <SupportTicketsView />;
      case 'audit':
        return <AuditLogsView />;
      case 'admin_team':
        return <AdminManagementView />;
      case 'admin_chat':
        return <AdminChatView />;
      case 'email_templates':
        return <EmailTemplatesView />;
      case 'profile_settings':
        return <ProfileSettingsView initialTab={profileTab} onTabChange={setProfileTab} />;
      default:
        return <DashboardView onNavigate={(v) => setActiveView(v)} />;
    }
  };

  return (
    <Shell 
      activeView={activeView} 
      onSelectView={setActiveView}
      onNavigateToProfile={handleNavigateToProfile}
    >
      {(view) => renderCurrentView(view)}
    </Shell>
  );
};

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
        <AdminApp />
      </AuthProvider>
    </ThemeProvider>
  );
}
